import React from "react";

import { CircleCheckBig, Download, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/src/components/ui/button";
import { cn } from "@/src/lib/utils";

import {
  AUDIO_FORMAT_KEYS,
  AUDIO_FORMAT_LIST,
  VIDEO_FORMAT_KEYS,
  VIDEO_FORMAT_LIST,
} from "../../../../constants/encoding";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { actionsColumn } from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../../dataTable/dataTableStyles";
import { ENCODING_PROFILES_ROUTE } from "../../../routes/routes";
import AudioVideoFormatTag from "../AudioVideoFormatTag";

import type { TFunction } from "i18next";

import type { EncodingProfile } from "../../../../api/EncoderAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface EncodingProfileColumnsOptions {
  t: TFunction;
  /** Option d'organisation `allow_fast` : sans elle, pas de colonne FAST. */
  hasFastOption: boolean;
  onEdit: (profile: EncodingProfile) => void;
  onDelete: (profile: EncodingProfile) => void;
  onChangeDefault: (profile: EncodingProfile) => void;
}

export function getEncodingProfileHref(uuid: string): string {
  return `${ENCODING_PROFILES_ROUTE}/${uuid}`;
}

const VIDEO_FILTER_FORMAT_LIST = [...VIDEO_FORMAT_LIST].reverse();
const AUDIO_FILTER_FORMAT_LIST = [...AUDIO_FORMAT_LIST].reverse();

function getActiveFormats(
  profile: EncodingProfile,
  formats: readonly string[],
  keys: Record<string, keyof EncodingProfile>,
): string[] {
  return formats.filter((format) => {
    const key = keys[format];
    return key !== undefined && profile[key] === true;
  });
}

function getVideoFormats(profile: EncodingProfile): string[] {
  return getActiveFormats(profile, VIDEO_FORMAT_LIST, VIDEO_FORMAT_KEYS);
}

function getAudioFormats(profile: EncodingProfile): string[] {
  return getActiveFormats(profile, AUDIO_FORMAT_LIST, AUDIO_FORMAT_KEYS);
}

function fastColumn(t: TFunction): DataTableColumnDef<EncodingProfile> {
  return {
    id: "fast",
    accessorFn: (profile) => profile.fast,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("encodingProfileList.fast")}
      />
    ),
    meta: {
      label: t("encodingProfileList.fast"),
      width: "8%",
      minWidth: 95,
      align: "center",
    },
    cell: ({ row }) =>
      row.original.fast ? (
        <span className="border-status-active/40 bg-status-active/10 text-status-active inline-block rounded border px-1 py-0.5 align-middle text-xs font-medium">
          {`${row.original.fast_framerate_target} fps`}
        </span>
      ) : null,
  };
}

export function encodingProfileColumns({
  t,
  hasFastOption,
  onEdit,
  onDelete,
  onChangeDefault,
}: EncodingProfileColumnsOptions): DataTableColumnDef<EncodingProfile>[] {
  return [
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("encodingProfileList.name")}
        />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell href={getEncodingProfileHref(row.original.uuid)}>
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    {
      id: "default",
      accessorFn: (profile) => profile.default,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("encodingProfileList.default")}
        />
      ),
      meta: {
        label: t("encodingProfileList.default"),
        width: "10%",
        minWidth: 125,
        align: "center",
      },
      cell: ({ row }) =>
        row.original.default ? (
          <span className="inline-flex align-middle">
            <CircleCheckBig
              aria-hidden="true"
              className={cn(CELL_ICON_CLASS, "text-primary")}
            />
            <span className="sr-only">{t("encodingProfileList.default")}</span>
          </span>
        ) : (
          <Button
            className="text-subtle hover:text-muted-foreground"
            onClick={() => onChangeDefault(row.original)}
            size="icon-sm"
            variant="ghost"
            aria-label={t("encodingProfileList.setAsDefault", {
              profileName: row.original.label,
            })}
          >
            <CircleCheckBig aria-hidden="true" className="size-5" />
          </Button>
        ),
    },
    {
      id: "videoFormats",
      accessorFn: getVideoFormats,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("encodingProfileList.videoFormats")}
        />
      ),
      enableSorting: false,
      meta: {
        label: t("encodingProfileList.videoFormats"),
        searchable: true,
        width: "25%",
        minWidth: 130,
        align: "center",
        filterOptions: VIDEO_FILTER_FORMAT_LIST.map((format) => ({
          value: format,
          label: t(`encodingProfileList.videoFormat.${format}`),
        })),
      },
      cell: ({ row }) => {
        const activeFormats = getVideoFormats(row.original);

        return (
          <div className="flex flex-wrap justify-center gap-1">
            {VIDEO_FORMAT_LIST.map((format) => (
              <AudioVideoFormatTag
                key={format}
                isActive={activeFormats.includes(format)}
              >
                {t(`encodingProfileList.videoFormat.${format}`)}
              </AudioVideoFormatTag>
            ))}
          </div>
        );
      },
    },
    {
      id: "audioFormats",
      accessorFn: getAudioFormats,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("encodingProfileList.audioFormats")}
        />
      ),
      enableSorting: false,
      meta: {
        label: t("encodingProfileList.audioFormats"),
        searchable: true,
        width: "18%",
        minWidth: 130,
        align: "center",
        filterOptions: AUDIO_FILTER_FORMAT_LIST.map((format) => ({
          value: format,
          label: t(`encodingProfileList.audioFormat.${format}`),
        })),
      },
      cell: ({ row }) => {
        const activeFormats = getAudioFormats(row.original);

        return (
          <div className="flex flex-wrap justify-center gap-1">
            {AUDIO_FORMAT_LIST.map((format) => (
              <AudioVideoFormatTag
                key={format}
                isActive={activeFormats.includes(format)}
              >
                {t(`encodingProfileList.audioFormat.${format}`)}
              </AudioVideoFormatTag>
            ))}
          </div>
        );
      },
    },
    {
      id: "downloadToGo",
      accessorFn: (profile) => profile.ott_download_to_go,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("encodingProfileList.downloadToGo")}
        />
      ),
      meta: {
        label: t("encodingProfileList.downloadToGo"),
        width: "13%",
        minWidth: 160,
        align: "center",
      },
      cell: ({ row }) =>
        row.original.ott_download_to_go ? (
          <span className="border-primary/20 bg-primary/10 text-primary inline-flex rounded border p-0.5 align-middle">
            <Download aria-hidden="true" className="size-4" />
            <span className="sr-only">
              {t("encodingProfileList.downloadToGo")}
            </span>
          </span>
        ) : null,
    },
    ...(hasFastOption ? [fastColumn(t)] : []),
    actionsColumn<EncodingProfile>(
      (profile) => [
        {
          label: t("encodingProfileList.edit"),
          icon: <Pencil />,
          onClick: () => onEdit(profile),
        },
        {
          label: t("encodingProfileList.delete"),
          icon: <Trash2 />,
          variant: "destructive",
          // Le profil par défaut ne se supprime pas.
          disabled: profile.default,
          onClick: () => onDelete(profile),
          confirm: {
            title: t("encodingProfileList.deleteProfileModalTitle"),
            description: t("encodingProfileList.deleteProfileModalText", {
              profileName: profile.label,
            }),
            confirmLabel: t("encodingProfileList.delete"),
          },
        },
      ],
      (profile) => profile.label,
    ),
  ];
}
